import { styled } from '@mui/material/styles';
import { Box, Button, TableCell, TableRow, Typography, Checkbox } from '@mui/material';

// --- Table components (adapted from ModulePreferences.styled.tsx) ---

export const StyledTableRow = styled(TableRow)(() => ({
  height: '48px',
  '&:hover': {
    backgroundColor: '#f0f0f0',
  },
  '& th': {
    fontWeight: 'bold',
    backgroundColor: 'white',
  },
}));

export const HeaderCell = styled(TableCell)(() => ({
  fontWeight: 'bold',
  '&:last-child': {
    borderRight: 'none',
  },
}));

export const SelectAllText = styled(Box)(() => ({
  color: '#00BCD4',
  fontSize: '0.75rem',
  cursor: 'pointer',
  marginTop: '4px',
  textAlign: 'right',
  whiteSpace: 'nowrap',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const StyledCheckbox = styled(Checkbox)({
  color: '#00BCD4',
  '&.Mui-checked': {
    color: '#00BCD4',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  },
  padding: '4px',
});

export const StyledTableCell = styled(TableCell)<{ columnIndex?: number }>(({ columnIndex }) => ({
  padding: '4px 8px',
  fontSize: '14px',
  borderBottom: 'none',
  backgroundColor: columnIndex === 1 ? '#E7EFF5' : 'white',
  '&:last-child': {
    borderRight: 'none',
  },
}));

// --- Action buttons ---

const actionButtonBase = {
  textTransform: 'uppercase' as const,
  fontWeight: 'bold' as const,
  borderRadius: '24px',
  fontSize: '0.75rem',
  padding: '4px 14px',
  whiteSpace: 'nowrap' as const,
};

export const SaveButton = styled(Button)(() => ({
  ...actionButtonBase,
  backgroundColor: '#f0f7fa',
  color: '#000',
  '&:hover': {
    backgroundColor: '#e0eef5',
  },
}));

export const ResetButton = styled(Button)(() => ({
  ...actionButtonBase,
  backgroundColor: '#fff',
  color: '#666',
  border: '1px solid #CDD6D9',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

export const DangerButton = styled(Button)(() => ({
  ...actionButtonBase,
  backgroundColor: '#fff',
  color: '#d32f2f',
  border: '1px solid #d32f2f',
  '&:hover': {
    backgroundColor: '#fef2f2',
    borderColor: '#b71c1c',
  },
  '&.Mui-disabled': {
    color: '#ccc',
    borderColor: '#e0e0e0',
  },
}));

export const ImportButton = styled(Button)(() => ({
  ...actionButtonBase,
  backgroundColor: '#fff',
  color: '#1976d2',
  border: '1px solid #1976d2',
  '&:hover': {
    backgroundColor: '#e3f2fd',
    borderColor: '#1565c0',
  },
  '&.Mui-disabled': {
    color: '#ccc',
    borderColor: '#e0e0e0',
  },
}));

export const CopyButton = styled(Button)(() => ({
  ...actionButtonBase,
  backgroundColor: '#fff',
  color: '#2e7d32',
  border: '1px solid #2e7d32',
  '&:hover': {
    backgroundColor: '#e8f5e9',
    borderColor: '#1b5e20',
  },
  '&.Mui-disabled': {
    color: '#ccc',
    borderColor: '#e0e0e0',
  },
}));

export const ToggleAllButton = styled(Button)(() => ({
  ...actionButtonBase,
  backgroundColor: '#fff',
  color: '#6a1b9a',
  border: '1px solid #6a1b9a',
  '&:hover': {
    backgroundColor: '#f3e5f5',
    borderColor: '#4a148c',
  },
  '&.Mui-disabled': {
    color: '#ccc',
    borderColor: '#e0e0e0',
  },
}));

// --- Section containers ---

export const GlobalActionsBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(2),
  borderRadius: '8px',
  backgroundColor: '#f8fafb',
  border: '1px solid #e0e7ec',
  maxWidth: '770px',
}));

export const SectionLabel = styled(Typography)(() => ({
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#8a9aa5',
  marginRight: '4px',
  whiteSpace: 'nowrap',
}));

// --- Typography / sections ---

export const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  fontWeight: 'bold',
}));

export const FootnoteContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  color: '#666',
  fontSize: '0.875rem',
}));

export const FootnoteText = styled(Typography)(() => ({
  color: 'textSecondary',
  fontSize: '0.875rem',
  lineHeight: 1.5,
}));

